import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';

// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import {
  Button as JigsawButton,
  WhiteLabelPaletteName,
  WhiteLabelThemeProvider,
} from '@solvhealth/jigsaw-v1';
import { colors } from '../../constants/colors';
import { fontSize, fontFamily } from '../../constants/text';
import TextField from '../SolvPatternLibrary/TextField';
import SecurityShield from '../SolvPatternLibrary/Icons/SecurityShield';
import { getNameInitialsFromFullName, isEmptyString } from '../../core/util/string';
import { analyticsTrackEvent } from '../../core/analytics';
import {
  VERIFY_PROFILE_VERIFICATION_FAILED,
  VERIFY_PROFILE_VIEW_VERIFICATION_SCREEN,
} from '../../core/analytics/events';
import { VerifyProfileSagas } from '../../sagas/verifyProfile';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';

const Root = styled.div.withConfig({ componentId: 'sc-19a2cg6-0' })`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
`;

const ShieldIcon = styled(SecurityShield).withConfig({ componentId: 'sc-19a2cg6-1' })`
  font-size: 120px;
`;

const Title = styled.h2.withConfig({ componentId: 'sc-19a2cg6-2' })`
  color: ${colors.squid};
  font-size: ${fontSize.extraExtraLarge};
  font-family: ${fontFamily.bold};
  letter-spacing: -0.05px;
  text-align: center;
`;

const Info = styled.p.withConfig({ componentId: 'sc-19a2cg6-3' })`
  color: ${colors.lightText};
  font-size: ${fontSize.default};
  text-align: center;
`;

const Form = styled.form.withConfig({ componentId: 'sc-19a2cg6-4' })`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 370px;
`;

const LastNameField = styled(TextField).withConfig({ componentId: 'sc-19a2cg6-5' })`
  display: block;
  flex-direction: unset;
`;

/**
 * Jigsaw Button is used for the classic solid background button, which should be crunchberry in all cases
 * except for whitelabelled cases where it should be whatever the theme palette color is
 */
const SubmitButton = styled(JigsawButton).withConfig({ componentId: 'sc-19a2cg6-6' })`
  margin-top: 10px;
  width: 100%;
`;

/**
 * The Material-UI SolvPatternLibrary Button is used for the ButtonVariations.link setting, where it's a
 * plain button (no background color and no outline) wth blue text that looks and behaves like a link. The
 * jigsaw library does support a button that behaves like this (variant="text") but doesn't handle the blue
 * color case so for now there's two buttons here
 */
const SkipButton = styled(Button).withConfig({ componentId: 'sc-19a2cg6-7' })`
  text-transform: unset;
  width: 100%;
  margin: 20px 0;
`;

const mapStateToProps = (state: any) => ({
  verificationError: state.verifyProfile.verificationError,
  accountSummary: state.account.summary || {},
  palette: state.theme?.palette,
});

const mapDispatchToProps = (dispatch: any) => ({
  hideUnrecognizedUserProfile: (userProfileId: any, onSuccess: any) =>
    dispatch({ type: VerifyProfileSagas.HIDE_UNRECOGNIZED_PROFILE, userProfileId, onSuccess }),

  verifyLastName: (accountId: any, userProfileId: any, lastNameInput: any, onSuccess: any) =>
    dispatch({
      type: VerifyProfileSagas.VERIFY_LAST_NAME,
      accountId,
      userProfileId,
      lastNameInput,
      onSuccess,
    }),
});

type EnterLastNameProps = {
  accountSummary: any;
  form: formShape;
  onSkip?: (...args: any[]) => any;
  onVerifySuccess?: (...args: any[]) => any;
  hideUnrecognizedUserProfile: (...args: any[]) => any;
  verifyLastName?: (...args: any[]) => any;
  verificationError?: any;
  profileToVerify?: any;
  palette?: WhiteLabelPaletteName;
};

type EnterLastNameState = any;

class EnterLastName extends PureComponent<EnterLastNameProps, EnterLastNameState> {
  initials: any;

  trackingProperties: any;

  constructor(props: EnterLastNameProps) {
    super(props);
    this.state = { verificationFailed: false };
    this.initials = getNameInitialsFromFullName(
      `${this.props.profileToVerify.first_name} ${this.props.profileToVerify.last_name}`,
      ''
    ).toUpperCase();
    this.trackingProperties = {
      profile_id: this.props.profileToVerify.user_profile_id,
      account_id: this.props.profileToVerify.account_id,
    };
  }

  componentDidMount = () => {
    analyticsTrackEvent(VERIFY_PROFILE_VIEW_VERIFICATION_SCREEN, this.trackingProperties);
  };

  onSubmit = (e: any) => {
    e.preventDefault();
    const { form, onVerifySuccess, accountSummary, verifyLastName } = this.props;
    form.validateFields((error: any, values: any) => {
      if (error) {
        this.setState({ verificationFailed: true });
        analyticsTrackEvent(VERIFY_PROFILE_VERIFICATION_FAILED, this.trackingProperties);
      } else {
        this.setState({ verificationFailed: false });

        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        verifyLastName(
          accountSummary.id,
          this.props.profileToVerify.user_profile_id,
          values.lastName,
          onVerifySuccess
        );
      }
    });
  };

  onClickSkipButton = () => {
    const { onSkip, hideUnrecognizedUserProfile } = this.props;
    hideUnrecognizedUserProfile(this.props.profileToVerify.user_profile_id, onSkip);
  };

  render = () => {
    const { verificationFailed } = this.state;
    const { form, verificationError, palette } = this.props;
    const error = form.getFieldError('lastName');

    const verifyProfileLastName = (rule: any, value: any, callback: any) => {
      if (!value || isEmptyString(value)) {
        callback('Last name required');
        return;
      }

      callback();
    };

    return (
      <WhiteLabelThemeProvider theme={palette || 'crunchberry'}>
        <Root>
          <ShieldIcon />

          <Title>We value your privacy</Title>

          <Info>
            Your security is our priority. To continue, please confirm {this.initials}
            's last name.
          </Info>

          <Form onSubmit={this.onSubmit}>
            {form.getFieldDecorator('lastName', {
              initialValue: '',
              validateTrigger: ['onBlur'],
              rules: [{ validator: verifyProfileLastName }],
            })(
              <LastNameField
                error={verificationFailed || Boolean(verificationError)}
                fullWidth
                helperText={<span>{Array.isArray(error) && error[0]}</span>}
                label={`Enter ${this.initials}'s Last Name`}
              />
            )}
            <SubmitButton type="submit">Continue</SubmitButton>
            <SkipButton onClick={this.onClickSkipButton} variation={ButtonVariations.link}>
              I don't recognize these initials
            </SkipButton>
          </Form>
        </Root>
      </WhiteLabelThemeProvider>
    );
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(createForm()(EnterLastName));
